// ============================================================================
// AboutElizavetaSection — Phase 18 (clinic-rework) — reusable across pages
// ----------------------------------------------------------------------------
// "Hi, I'm Elizaveta." section. Two-column on desktop (carousel left, text +
// CTA right), stacked on mobile. The shared opening / credibility / closing
// / CTA copy is hardcoded here; the page-specific middle line and the
// inquiry source come in as props so each service page personalises itself.
//
// Carousel behavior (per spec 3c · 4a · 5b):
//   - Autoplay every 6s; paused on hover / focus.
//   - Autoplay disabled when prefers-reduced-motion: reduce.
//   - Touch-swipe on mobile, arrows + dots on desktop.
//   - 1 slide:           no arrows, no dots — single image / video only.
//   - 0 slides / unpublished doc: carousel column drops; the section
//                                  still renders as text + CTA only.
//   - Video slides:      muted + looped + playsInline; only the active
//                        slide's video plays (others paused & reset).
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Section, Kicker, HairlineBrass } from "@/components/Section";
import { useAboutElizavetaSlides } from "@/lib/content";
import { cn } from "@/lib/utils";

// ─── Page-specific middle lines (Phase 18 — keep these in one place so
//     future service pages import the canonical sentence rather than
//     duplicating copy). ────────────────────────────────────────────────────
export const ABOUT_MIDDLE = {
    clinic:
        "I photograph clinics, wellness spaces, providers, treatment-room details, skincare/product details, and short promotional content for practices that want to feel current, trusted, and easy to choose.",
    business:
        "I photograph founders, teams, workspaces, brand details, products, and short promotional content for businesses that need polished visuals for websites, Google profiles, social media, and marketing.",
    homeServices:
        "I photograph teams, vehicles, tools, job-site details, owner portraits, before-and-after work, and short promotional content for service companies that want to look established, professional, and easy to trust.",
    personal:
        "I photograph portraits, couples, engagements, seniors, events, and personal milestones for people who want images that feel elevated, natural, and worth keeping.",
};

// Autoplay interval — 6 s per spec.
const AUTOPLAY_MS = 6000;

// ─── Internal carousel ─────────────────────────────────────────────────────

function AboutCarousel({ slides, testIdPrefix }) {
    const count = slides.length;
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const videoRefs = useRef([]);
    const touchStartX = useRef(null);
    const touchDeltaX = useRef(0);

    // prefers-reduced-motion — disable autoplay
    const [reduceMotion, setReduceMotion] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
        const apply = () => setReduceMotion(mql.matches);
        apply();
        const handler = (e) => setReduceMotion(e.matches);
        if (mql.addEventListener) mql.addEventListener("change", handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener("change", handler);
            else mql.removeListener(handler);
        };
    }, []);

    const goTo = useCallback(
        (next) => {
            if (count === 0) return;
            setIndex(((next % count) + count) % count);
        },
        [count]
    );
    const goNext = useCallback(() => goTo(index + 1), [goTo, index]);
    const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);

    // Autoplay
    useEffect(() => {
        if (count <= 1 || paused || reduceMotion) return undefined;
        const t = setInterval(() => {
            setIndex((i) => (i + 1) % count);
        }, AUTOPLAY_MS);
        return () => clearInterval(t);
    }, [count, paused, reduceMotion]);

    // Only the active slide's video plays — others pause + reset.
    useEffect(() => {
        videoRefs.current.forEach((v, i) => {
            if (!v) return;
            if (i === index) {
                const p = v.play();
                if (p && typeof p.catch === "function") p.catch(() => {});
            } else {
                v.pause();
                try { v.currentTime = 0; } catch (_) { /* noop */ }
            }
        });
    }, [index, slides]);

    // Keyboard arrows
    const onKeyDown = useCallback(
        (e) => {
            if (count <= 1) return;
            if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
            else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
        },
        [count, goPrev, goNext]
    );

    // Touch swipe — only on touch devices (>= 30px horizontal wins)
    const onTouchStart = (e) => {
        if (count <= 1) return;
        touchStartX.current = e.touches[0].clientX;
        touchDeltaX.current = 0;
    };
    const onTouchMove = (e) => {
        if (touchStartX.current == null) return;
        touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    };
    const onTouchEnd = () => {
        if (touchStartX.current == null) return;
        const d = touchDeltaX.current;
        touchStartX.current = null;
        touchDeltaX.current = 0;
        if (Math.abs(d) < 30) return;
        if (d < 0) goNext(); else goPrev();
    };

    if (count === 0) return null;

    const showChrome = count > 1;

    return (
        <div
            data-testid={`${testIdPrefix}-carousel`}
            className="relative w-full"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onKeyDown={onKeyDown}
            role={showChrome ? "region" : undefined}
            aria-roledescription={showChrome ? "carousel" : undefined}
            aria-label={showChrome ? "Photos and videos of Elizaveta" : undefined}
            tabIndex={showChrome ? 0 : -1}
        >
            <div className="relative w-full overflow-hidden rounded-[3px] bg-[hsl(var(--lux-porcelain))] border border-border aspect-[4/5]">
                {slides.map((slide, i) => {
                    const active = i === index;
                    const common = {
                        className: cn(
                            "absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out",
                            active ? "opacity-100" : "opacity-0 pointer-events-none"
                        ),
                        "aria-hidden": active ? undefined : true,
                    };
                    if (slide.mediaType === "video") {
                        return (
                            <video
                                key={slide.id}
                                ref={(el) => { videoRefs.current[i] = el; }}
                                src={slide.videoSrc}
                                poster={slide.image}
                                muted
                                loop
                                playsInline
                                preload={active ? "metadata" : "none"}
                                aria-label={slide.alt}
                                data-testid={`${testIdPrefix}-slide-${i}`}
                                {...common}
                            />
                        );
                    }
                    return (
                        <img
                            key={slide.id}
                            src={slide.image}
                            srcSet={slide.srcSet || undefined}
                            sizes="(min-width: 768px) 40vw, 100vw"
                            alt={slide.alt}
                            loading={active ? "eager" : "lazy"}
                            decoding="async"
                            data-testid={`${testIdPrefix}-slide-${i}`}
                            {...common}
                        />
                    );
                })}
            </div>

            {showChrome ? (
                <>
                    {/* Arrows — desktop / hover, but available on touch too. */}
                    <button
                        type="button"
                        onClick={goPrev}
                        aria-label="Previous slide"
                        data-testid={`${testIdPrefix}-prev`}
                        className="absolute top-1/2 -translate-y-1/2 left-2 sm:left-3 inline-flex items-center justify-center h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm border border-border text-foreground/80 hover:text-foreground hover:bg-background transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lux-oxblood))]/60"
                    >
                        <ChevronLeft className="h-5 w-5" aria-hidden />
                    </button>
                    <button
                        type="button"
                        onClick={goNext}
                        aria-label="Next slide"
                        data-testid={`${testIdPrefix}-next`}
                        className="absolute top-1/2 -translate-y-1/2 right-2 sm:right-3 inline-flex items-center justify-center h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm border border-border text-foreground/80 hover:text-foreground hover:bg-background transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lux-oxblood))]/60"
                    >
                        <ChevronRight className="h-5 w-5" aria-hidden />
                    </button>

                    {/* Dots */}
                    <div
                        role="tablist"
                        aria-label="Choose slide"
                        data-testid={`${testIdPrefix}-dots`}
                        className="absolute left-1/2 -translate-x-1/2 bottom-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-background/70 backdrop-blur-sm border border-border"
                    >
                        {slides.map((s, i) => (
                            <button
                                key={s.id}
                                type="button"
                                role="tab"
                                aria-selected={i === index}
                                aria-label={`Go to slide ${i + 1} of ${count}`}
                                onClick={() => goTo(i)}
                                data-testid={`${testIdPrefix}-dot-${i}`}
                                className={cn(
                                    "h-1.5 rounded-full transition-all duration-200",
                                    i === index
                                        ? "w-4 bg-[hsl(var(--lux-oxblood))]"
                                        : "w-1.5 bg-foreground/35 hover:bg-foreground/55"
                                )}
                            />
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
}

// ─── Section ───────────────────────────────────────────────────────────────

/**
 * AboutElizavetaSection
 *
 * Props:
 *   - middleLine    (string, required) Page-specific paragraph that
 *                   sits between the shared credibility line and the
 *                   shared closing line. Pick the canonical sentence
 *                   from `ABOUT_MIDDLE.{clinic|business|homeServices|personal}`.
 *   - inquirySource (string, required) Value for the ?source= query on
 *                   the CTA's /inquire link (e.g. 'clinic-about').
 *   - testIdPrefix  (string, optional) Defaults to 'about-elizaveta'.
 *                   Override per page to namespace data-testid attributes.
 *   - heading       (string, optional) Defaults to "Hi, I'm Elizaveta."
 *                   Per spec the heading is shared, but the override is
 *                   here for safety if a future page wants a variant.
 *   - ctaPrefix     (string, optional) When the About section IS the
 *                   page's closing element (e.g. /aesthetic-clinic-
 *                   photography after the legacy final CTA was removed),
 *                   the caller can prepend a closing phrase to the
 *                   shared CTA line. E.g. ctaPrefix="Let's work together."
 *                   renders as: "Let's work together. Tell me what
 *                   you're planning, and I'll recommend the best
 *                   session." Pages that still ship their own final CTA
 *                   should omit this prop.
 */
export function AboutElizavetaSection({
    middleLine,
    inquirySource,
    testIdPrefix = "about-elizaveta",
    heading = "Hi, I'm Elizaveta.",
    ctaPrefix,
}) {
    const slides = useAboutElizavetaSlides();
    const hasMedia = slides && slides.length > 0;
    const inquireHref = useMemo(
        () => `/inquire?source=${encodeURIComponent(inquirySource)}`,
        [inquirySource]
    );

    // Shared copy fragments (Phase 18 brief — same on every page).
    const OPENING = "I'm Elizaveta, a photographer working between Arizona and New York.";
    const CREDIBILITY =
        "My work has been published internationally, including in Science Illustrated, but what I care about most is making images that feel honest, polished, and worth keeping.";
    const CLOSING =
        "Every shoot is shaped around the person or project in front of me: how the images should feel, where they will live, and what they need to communicate. I'll guide you through the process, help with direction, and make the final gallery feel ready to use, share, print, or build around.";
    const CTA_LINE = "Tell me what you're planning, and I'll recommend the best session.";

    return (
        <Section
            spacing="normal"
            className="border-t border-border"
            data-testid={testIdPrefix}
        >
            <div
                className={cn(
                    "grid grid-cols-1 gap-10 lg:gap-14 items-center",
                    hasMedia ? "lg:grid-cols-12" : ""
                )}
            >
                {hasMedia ? (
                    <div className="lg:col-span-5 mx-auto w-full max-w-[420px] lg:max-w-none">
                        <AboutCarousel slides={slides} testIdPrefix={testIdPrefix} />
                    </div>
                ) : null}

                <div
                    className={cn(
                        hasMedia
                            ? "lg:col-span-7"
                            : "mx-auto w-full max-w-[720px] text-center"
                    )}
                >
                    <Kicker>Meet the photographer</Kicker>
                    <h2
                        className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-3"
                        data-testid={`${testIdPrefix}-heading`}
                    >
                        {heading}
                    </h2>
                    <HairlineBrass className={cn("mt-5", hasMedia ? "" : "mx-auto")} />

                    <div
                        className={cn(
                            "mt-6 space-y-4 text-[15px] sm:text-base leading-[1.85] text-foreground/85",
                            hasMedia ? "max-w-[58ch]" : "mx-auto max-w-[62ch]"
                        )}
                        data-testid={`${testIdPrefix}-body`}
                    >
                        <p>
                            <span data-testid={`${testIdPrefix}-opening`}>{OPENING}</span>{" "}
                            <span data-testid={`${testIdPrefix}-credibility`}>{CREDIBILITY}</span>
                        </p>
                        <p data-testid={`${testIdPrefix}-middle`}>{middleLine}</p>
                        <p data-testid={`${testIdPrefix}-closing`}>{CLOSING}</p>
                    </div>

                    <p
                        data-testid={`${testIdPrefix}-cta-line`}
                        className={cn(
                            "mt-6 text-[15px] leading-[1.7] text-foreground/85",
                            hasMedia ? "" : "mx-auto"
                        )}
                    >
                        {ctaPrefix ? (
                            <span
                                data-testid={`${testIdPrefix}-cta-prefix`}
                                className="font-serif-display text-foreground"
                            >
                                {ctaPrefix}
                            </span>
                        ) : null}
                        {ctaPrefix ? " " : null}
                        {CTA_LINE}
                    </p>

                    <div
                        className={cn(
                            "mt-6 flex",
                            hasMedia ? "" : "justify-center"
                        )}
                    >
                        <Link
                            to={inquireHref}
                            data-testid={`${testIdPrefix}-cta`}
                            onClick={() => {
                                if (typeof window !== "undefined") {
                                    window.scrollTo({ top: 0, behavior: "instant" });
                                }
                            }}
                            className="btn-request btn-request--sm"
                        >
                            Request Availability
                        </Link>
                    </div>
                </div>
            </div>
        </Section>
    );
}

export default AboutElizavetaSection;
